import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';

//! WTF is this component using this?
import { StyledInputButton } from '@modules/conversation-message/ConversationMessageInput/styles';
import { Stack } from '@mui/system';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record';
import { Timer } from '../Timer';
import Iconify from '../iconify/Iconify';

type AudioRecorderProps = {
  onStop: () => void;
  onSend: (file: File) => void;
  autoRecord?: boolean;
};

export const AudioRecorder = ({
  onStop,
  onSend,
  autoRecord,
}: AudioRecorderProps) => {
  const theme = useTheme();
  const [isRecording, setIsRecording] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const startDateRef = useRef(new Date().toISOString());
  const containerRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer>(null);
  const recordRef = useRef<RecordPlugin>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!containerRef.current) return () => {};

    const wave = WaveSurfer.create({
      container: containerRef.current,
      waveColor: theme.palette.primary.main,
      progressColor: theme.palette.primary.main,
      // responsive: true,
      height: 16,
      barHeight: 6,
      barGap: 3,
      barWidth: 2,
      barRadius: 1,
      cursorWidth: 0,
      interact: false,
    });

    const record = wave.registerPlugin(
      RecordPlugin.create({
        scrollingWaveform: true,
        mimeType: 'audio/mp4',
        audioBitsPerSecond: 128000,
      }),
    );

    // @ts-ignore
    wavesurferRef.current = wave;
    // @ts-ignore
    recordRef.current = record;

    return () => {
      wave?.destroy();
      // @ts-ignore
      wavesurferRef.current = null;
      // @ts-ignore
      recordRef.current = null;
    };
  }, [containerRef, theme.palette.grey, theme.palette.secondary.main]);

  useEffect(() => {
    if (autoRecord) {
      startRecording();
    }
  }, [autoRecord]);

  const sendRecording = async () => {
    if (!recordRef.current) return;

    recordRef.current.once('record-end', async (blob) => {
      const file = new File([blob], `audio.mp4`, { type: 'audio/mp4' });
      onSend(file);
      setIsRecording(false);
    });

    // wavesurferRef.current?.stop();
    recordRef.current.stopRecording();
  };

  const startRecording = () => {
    if (!recordRef.current) return;

    recordRef.current.startRecording().then(() => {
      setIsRecording(true);
      setIsReady(true);
    });
  };

  const handleSend = () => {
    if (isRecording) {
      sendRecording();
    } else {
      startRecording();
    }
  };

  return (
    <Stack direction="row" alignItems="center" flexGrow={1}>
      <StyledInputButton onClick={onStop} color="error" size="small">
        <Iconify icon="tabler:trash" />
      </StyledInputButton>
      <Stack
        direction="row"
        alignItems="center"
        flexGrow={1}
        gap={1}
        mx={1}
        sx={{
          px: 1.5,
          borderRadius: 1,
          flexGrow: 1,
          flexShrink: 0,
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          height: 'auto',
          minWidth: 150,
        }}
      >
        <Box
          sx={{
            borderRadius: '50%',
            width: 12,
            height: 12,
            minWidth: 12,
            backgroundColor:
              isReady && isRecording
                ? theme.palette.error.main
                : theme.palette.text.disabled,
          }}
        />
        <Box width={46}>
          <Typography variant="body2">
            <Timer startDate={startDateRef.current} play={isRecording} />
          </Typography>
        </Box>
        <Box
          ref={containerRef}
          sx={{
            ml: 0.5,
            width: '100%',
            height: 16,
          }}
        />
      </Stack>
      <StyledInputButton onClick={handleSend}>
        <Iconify icon="tabler:send-2" />
      </StyledInputButton>
    </Stack>
  );
};
